import type { ReadonlyVec3 } from "gl-matrix";
import { mat4, quat, vec3 } from "gl-matrix";
import { useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { computeOrthoNearFar, orthoCamActions } from "features/orthoCam";
import { selectIsProcoreDesign } from "slices/explorer/selectors";
import { ViewMode } from "types/misc";

import {
    renderActions,
    selectCamera,
    selectCameraDefaultFlyTime,
    selectCameraDefaults,
    selectViewMode,
} from "../renderSlice";
import { CameraType } from "../types";

export const DEFAULT_FLY_TIME = 1000;

export function useHandleCameraState() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const state = useAppSelector(selectCamera);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const defaults = useAppSelector(selectCameraDefaults);
    const defaultFlyTime = useAppSelector(selectCameraDefaultFlyTime);
    const defaultFlyTimeRef = useRef(defaultFlyTime);
    defaultFlyTimeRef.current = defaultFlyTime;
    const usePointerLockPinholeRef = useRef(defaults.pinhole.usePointerLock);
    usePointerLockPinholeRef.current = defaults.pinhole.usePointerLock;
    const viewMode = useAppSelector(selectViewMode);
    const dispatch = useAppDispatch();
    const orthoFixToAxis =
        state.type === CameraType.Orthographic ? state.fixOrthoCameraToBestAxisAlignedView : undefined;

    const pinholeKind = useRef<"flight" | "special" | "cadMiddlePan" | "cadRightPan" | "panorama" | "hover">(
        defaults.pinhole.controller,
    );

    useEffect(() => {
        if (!view) {
            return;
        }

        pinholeKind.current = viewMode === ViewMode.Panorama ? "panorama" : defaults.pinhole.controller;
    }, [view, viewMode, defaults.pinhole.controller, pinholeKind]);

    useEffect(() => {
        swapCamera();
        async function swapCamera() {
            if (!view) {
                return;
            }

            const flyTime = state.goTo?.flyTime ?? defaultFlyTimeRef.current;

            if (state.type === CameraType.Pinhole) {
                const controller = view.controllers[pinholeKind.current];

                // When we have 0 flyTime and do both switchCameraController and then moveTo -
                // the camera makes a bad jump.
                const fovChanged = state.goTo?.fov !== undefined && state.goTo.fov !== controller.fov;
                const instantMove = flyTime === 0 || fovChanged;

                if (fovChanged) {
                    // Change fov before switching controller, otherwise it won't be respected.
                    // NOTE: switchCameraController accepts fov, but it's only for ortho fov.
                    controller.init({ kind: controller.kind, fovDegrees: state.goTo!.fov });
                }
                view.switchCameraController(
                    pinholeKind.current,
                    instantMove && state.goTo
                        ? {
                              position: vec3.clone(state.goTo.position),
                              rotation: quat.clone(state.goTo.rotation),
                          }
                        : undefined,
                );

                if (state.goTo && !instantMove) {
                    controller.moveTo(
                        vec3.clone(state.goTo.position),
                        flyTime ?? DEFAULT_FLY_TIME,
                        quat.clone(state.goTo.rotation),
                    );
                } else if (state.zoomTo) {
                    controller.zoomTo(state.zoomTo, flyTime);
                }

                controller.input.usePointerLock = usePointerLockPinholeRef.current;
                dispatch(renderActions.setGrid({ enabled: false }));
            } else {
                if (state.goTo && flyTime) {
                    await view.switchCameraController("ortho");
                    const controller = view.controllers["ortho"];
                    controller.moveTo(vec3.clone(state.goTo.position), flyTime, quat.clone(state.goTo.rotation));
                } else {
                    await view.switchCameraController(
                        "ortho",
                        state.goTo
                            ? {
                                  position: vec3.clone(state.goTo.position),
                                  rotation: quat.clone(state.goTo.rotation),
                                  fov: state.goTo.fov,
                              }
                            : undefined,
                    );
                }

                dispatch(orthoCamActions.setIsTopDown(view.isTopDown()));
                const mat = mat4.fromQuat(mat4.create(), state.goTo?.rotation ?? view.renderState.camera.rotation);
                const right = vec3.fromValues(mat[0], mat[1], mat[2]);
                const up = vec3.fromValues(mat[4], mat[5], mat[6]);

                const gridOrigin = state.gridOrigo ?? state.goTo?.position;
                if (gridOrigin) {
                    dispatch(
                        renderActions.setGrid({
                            origin: gridOrigin,
                            axisY: up,
                            axisX: right,
                        }),
                    );
                }
            }
        }
    }, [view, dispatch, state]);

    useEffect(() => {
        if (!view) {
            return;
        }

        if (state.type === CameraType.Pinhole) {
            const clipping = {
                ...(state.goTo?.near ? { near: state.goTo.near } : {}),
                ...(state.goTo?.far ? { far: state.goTo.far } : {}),
            };
            view.modifyRenderState({
                points: { useProjectedPosition: false },
                camera: { ...defaults.pinhole.clipping, ...clipping },
            });
        } else {
            // When no explicit near/far is provided (e.g. axis sphere navigation dispatches
            // only position/rotation), recompute the slab from the target position so geometry
            // is never clipped after in-ortho-mode navigations.
            const hasExplicitNear = state.goTo?.near !== undefined;
            const hasExplicitFar = state.goTo?.far !== undefined;
            let clipping: { near?: number; far?: number };
            if (hasExplicitNear || hasExplicitFar) {
                clipping = {
                    ...(hasExplicitNear ? { near: state.goTo!.near } : {}),
                    ...(hasExplicitFar ? { far: state.goTo!.far } : {}),
                };
            } else {
                const pos = (state.goTo?.position ?? view.renderState.camera.position) as ReadonlyVec3;
                if (isProcoreDesign) {
                    clipping = computeOrthoNearFar(pos, view);
                } else {
                    clipping = defaults.orthographic.clipping;
                }
            }
            view.modifyRenderState({
                points: { useProjectedPosition: view.isTopDown() },
                camera: { ...defaults.orthographic.clipping, ...clipping },
            });
        }
    }, [state, defaults.pinhole.clipping, defaults.orthographic.clipping, view, isProcoreDesign]);

    useEffect(() => {
        if (!view) {
            return;
        }

        const orbitOnPrimaryButton = defaults.pinhole.controller === "cadMiddlePan";
        const blockOrthoMouseRotate = orthoFixToAxis === true;
        view.controllers["ortho"].updateParams({
            usePointerLock: defaults.orthographic.usePointerLock,
            deAcceleration: defaults.orthographic.deAcceleration,
            touchRotate: defaults.orthographic.touchRotate,
            mouseRotate: defaults.orthographic.mouseRotate && !blockOrthoMouseRotate,
            rotationalVelocity: defaults.orthographic.rotationalVelocity,
            orbitOnPrimaryButton,
        });
    }, [
        view,
        defaults.orthographic.usePointerLock,
        defaults.orthographic.deAcceleration,
        defaults.orthographic.touchRotate,
        defaults.orthographic.mouseRotate,
        defaults.orthographic.rotationalVelocity,
        defaults.pinhole.controller,
        orthoFixToAxis,
    ]);

    useEffect(() => {
        if (!view || view.renderState.camera.kind !== "pinhole") {
            return;
        }

        view.controllers[pinholeKind.current].input.usePointerLock = defaults.pinhole.usePointerLock;
    }, [view, defaults.pinhole.usePointerLock]);
}
